// src/dictionary.ts

export interface CharEntry {
  char: string;
  tone: number; // 1-5, 5 = neutral tone
}

// Full pinyin -> characters mapping (base syllable without tone number)
// Each array contains { char, tone } entries sorted by tone (1,2,3,4,5).
const raw: Record<string, CharEntry[]> = {
  a: [
    { char: "啊", tone: 1 },
    { char: "阿", tone: 1 },
    { char: "吖", tone: 1 },
    { char: "嗄", tone: 2 },
    { char: "啊", tone: 3 },
    { char: "啊", tone: 4 },
    { char: "啊", tone: 5 },
  ],
  ai: [
    { char: "哀", tone: 1 },
    { char: "挨", tone: 1 },
    { char: "埃", tone: 1 },
    { char: "唉", tone: 1 },
    { char: "哎", tone: 1 },
    { char: "癌", tone: 2 },
    { char: "皑", tone: 2 },
    { char: "矮", tone: 3 },
    { char: "蔼", tone: 3 },
    { char: "爱", tone: 4 },
    { char: "碍", tone: 4 },
    { char: "艾", tone: 4 },
    { char: "隘", tone: 4 },
  ],
  an: [
    { char: "安", tone: 1 },
    { char: "氨", tone: 1 },
    { char: "鞍", tone: 1 },
    { char: "庵", tone: 1 },
    { char: "俺", tone: 3 },
    { char: "按", tone: 4 },
    { char: "暗", tone: 4 },
    { char: "岸", tone: 4 },
    { char: "案", tone: 4 },
    { char: "黯", tone: 4 },
  ],
  ang: [
    { char: "肮", tone: 1 },
    { char: "昂", tone: 2 },
    { char: "盎", tone: 4 },
  ],
  ao: [
    { char: "凹", tone: 1 },
    { char: "熬", tone: 1 }, // 熬 also tone 2
    { char: "敖", tone: 2 },
    { char: "翱", tone: 2 },
    { char: "熬", tone: 2 },
    { char: "袄", tone: 3 },
    { char: "奥", tone: 4 },
    { char: "澳", tone: 4 },
    { char: "傲", tone: 4 },
    { char: "懊", tone: 4 },
  ],
  ba: [
    { char: "八", tone: 1 },
    { char: "巴", tone: 1 },
    { char: "吧", tone: 1 },
    { char: "疤", tone: 1 },
    { char: "笆", tone: 1 },
    { char: "拔", tone: 2 },
    { char: "跋", tone: 2 },
    { char: "把", tone: 3 },
    { char: "靶", tone: 3 },
    { char: "爸", tone: 4 },
    { char: "罢", tone: 4 },
    { char: "霸", tone: 4 },
    { char: "坝", tone: 4 },
    { char: "吧", tone: 4 },
  ],
  bai: [
    { char: "掰", tone: 1 },
    { char: "白", tone: 2 },
    { char: "百", tone: 3 },
    { char: "柏", tone: 3 },
    { char: "摆", tone: 3 },
    { char: "败", tone: 4 },
    { char: "拜", tone: 4 },
    { char: "稗", tone: 4 },
  ],
  ban: [
    { char: "班", tone: 1 },
    { char: "般", tone: 1 },
    { char: "搬", tone: 1 },
    { char: "颁", tone: 1 },
    { char: "斑", tone: 1 },
    { char: "版", tone: 3 },
    { char: "板", tone: 3 },
    { char: "办", tone: 4 },
    { char: "半", tone: 4 },
    { char: "伴", tone: 4 },
    { char: "扮", tone: 4 },
    { char: "瓣", tone: 4 },
    { char: "绊", tone: 4 },
  ],
  bang: [
    { char: "帮", tone: 1 },
    { char: "邦", tone: 1 },
    { char: "绑", tone: 3 },
    { char: "膀", tone: 3 },
    { char: "榜", tone: 3 },
    { char: "棒", tone: 4 },
    { char: "傍", tone: 4 },
    { char: "磅", tone: 4 },
    { char: "蚌", tone: 4 },
  ],
  bao: [
    { char: "包", tone: 1 },
    { char: "胞", tone: 1 },
    { char: "薄", tone: 1 },
    { char: "褒", tone: 1 },
    { char: "薄", tone: 2 },
    { char: "雹", tone: 2 },
    { char: "保", tone: 3 },
    { char: "饱", tone: 3 },
    { char: "宝", tone: 3 },
    { char: "堡", tone: 3 },
    { char: "报", tone: 4 },
    { char: "抱", tone: 4 },
    { char: "暴", tone: 4 },
    { char: "爆", tone: 4 },
    { char: "鲍", tone: 4 },
  ],
  bei: [
    { char: "杯", tone: 1 },
    { char: "背", tone: 1 },
    { char: "悲", tone: 1 },
    { char: "卑", tone: 1 },
    { char: "北", tone: 3 },
    { char: "被", tone: 4 },
    { char: "备", tone: 4 },
    { char: "背", tone: 4 },
    { char: "辈", tone: 4 },
    { char: "贝", tone: 4 },
    { char: "倍", tone: 4 },
    { char: "臂", tone: 4 },
  ],
  ben: [
    { char: "奔", tone: 1 },
    { char: "本", tone: 3 },
    { char: "笨", tone: 4 },
    { char: "奔", tone: 4 },
    { char: "夯", tone: 4 },
  ],
  beng: [
    { char: "崩", tone: 1 },
    { char: "甭", tone: 2 },
    { char: "绷", tone: 3 },
    { char: "蹦", tone: 4 },
    { char: "泵", tone: 4 },
  ],
  bi: [
    { char: "逼", tone: 1 },
    { char: "鼻", tone: 2 },
    { char: "比", tone: 3 },
    { char: "笔", tone: 3 },
    { char: "彼", tone: 3 },
    { char: "鄙", tone: 3 },
    { char: "必", tone: 4 },
    { char: "毕", tone: 4 },
    { char: "闭", tone: 4 },
    { char: "币", tone: 4 },
    { char: "避", tone: 4 },
    { char: "壁", tone: 4 },
    { char: "臂", tone: 4 },
    { char: "璧", tone: 4 },
    { char: "庇", tone: 4 },
  ],
  bian: [
    { char: "边", tone: 1 },
    { char: "编", tone: 1 },
    { char: "鞭", tone: 1 },
    { char: "扁", tone: 3 },
    { char: "变", tone: 4 },
    { char: "便", tone: 4 },
    { char: "遍", tone: 4 },
    { char: "辩", tone: 4 },
    { char: "辨", tone: 4 },
  ],
  biao: [
    { char: "标", tone: 1 },
    { char: "彪", tone: 1 },
    { char: "表", tone: 3 },
  ],
  bie: [
    { char: "憋", tone: 1 },
    { char: "鳖", tone: 1 },
    { char: "别", tone: 2 },
    { char: "别", tone: 4 },
    { char: "别扭", tone: 4 }, // 拗
  ],
  bin: [
    { char: "宾", tone: 1 },
    { char: "滨", tone: 1 },
    { char: "彬", tone: 1 },
    { char: "殡", tone: 4 },
    { char: "鬓", tone: 4 },
  ],
  bing: [
    { char: "冰", tone: 1 },
    { char: "兵", tone: 1 },
    { char: "丙", tone: 3 },
    { char: "饼", tone: 3 },
    { char: "柄", tone: 3 },
    { char: "秉", tone: 3 },
    { char: "并", tone: 4 },
    { char: "病", tone: 4 },
  ],
  bo: [
    { char: "拨", tone: 1 },
    { char: "波", tone: 1 },
    { char: "播", tone: 1 },
    { char: "玻", tone: 1 },
    { char: "伯", tone: 2 },
    { char: "博", tone: 2 },
    { char: "薄", tone: 2 },
    { char: "脖", tone: 2 },
    { char: "驳", tone: 2 },
    { char: "勃", tone: 2 },
    { char: "跛", tone: 3 },
    { char: "薄", tone: 4 },
    { char: "簸", tone: 4 },
  ],
  bu: [
    { char: "不", tone: 4 },
    { char: "布", tone: 4 },
    { char: "步", tone: 4 },
    { char: "部", tone: 4 },
    { char: "簿", tone: 4 },
    { char: "怖", tone: 4 },
    { char: "卜", tone: 3 },
    { char: "补", tone: 3 },
    { char: "捕", tone: 3 },
    { char: "不", tone: 2 }, // before 4th tone, but we keep it as tone 4?
  ],
  ca: [{ char: "擦", tone: 1 }],
  cai: [
    { char: "猜", tone: 1 },
    { char: "才", tone: 2 },
    { char: "财", tone: 2 },
    { char: "材", tone: 2 },
    { char: "裁", tone: 2 },
    { char: "采", tone: 3 },
    { char: "彩", tone: 3 },
    { char: "踩", tone: 3 },
    { char: "睬", tone: 3 },
    { char: "菜", tone: 4 },
    { char: "蔡", tone: 4 },
  ],
  can: [
    { char: "参", tone: 1 },
    { char: "餐", tone: 1 },
    { char: "蚕", tone: 2 },
    { char: "残", tone: 2 },
    { char: "惨", tone: 3 },
    { char: "灿", tone: 4 },
    { char: "璨", tone: 4 },
  ],
  cang: [
    { char: "仓", tone: 1 },
    { char: "苍", tone: 1 },
    { char: "舱", tone: 1 },
    { char: "藏", tone: 2 },
  ],
  cao: [
    { char: "操", tone: 1 },
    { char: "糙", tone: 1 },
    { char: "曹", tone: 2 },
    { char: "槽", tone: 2 },
    { char: "草", tone: 3 },
  ],
  ce: [
    { char: "侧", tone: 4 },
    { char: "测", tone: 4 },
    { char: "策", tone: 4 },
    { char: "册", tone: 4 },
  ],
  cen: [
    { char: "参", tone: 1 },
    { char: "岑", tone: 2 },
  ],
  ceng: [
    { char: "层", tone: 2 },
    { char: "曾", tone: 2 },
    { char: "蹭", tone: 4 },
  ],
  cha: [
    { char: "差", tone: 1 },
    { char: "插", tone: 1 },
    { char: "叉", tone: 1 },
    { char: "查", tone: 2 },
    { char: "察", tone: 2 },
    { char: "茶", tone: 2 },
    { char: "茬", tone: 2 },
    { char: "差", tone: 4 },
    { char: "刹", tone: 4 },
  ],
  chai: [
    { char: "拆", tone: 1 },
    { char: "柴", tone: 2 },
  ],
  chan: [
    { char: "搀", tone: 1 },
    { char: "蝉", tone: 2 },
    { char: "馋", tone: 2 },
    { char: "缠", tone: 2 },
    { char: "产", tone: 3 },
    { char: "铲", tone: 3 },
    { char: "颤", tone: 4 },
    { char: "忏", tone: 4 },
  ],
  chang: [
    { char: "昌", tone: 1 },
    { char: "长", tone: 2 },
    { char: "常", tone: 2 },
    { char: "尝", tone: 2 },
    { char: "偿", tone: 2 },
    { char: "场", tone: 2 },
    { char: "厂", tone: 3 },
    { char: "场", tone: 3 },
    { char: "唱", tone: 4 },
    { char: "畅", tone: 4 },
    { char: "倡", tone: 4 },
  ],
  chao: [
    { char: "超", tone: 1 },
    { char: "抄", tone: 1 },
    { char: "朝", tone: 2 },
    { char: "潮", tone: 2 },
    { char: "嘲", tone: 2 },
    { char: "巢", tone: 2 },
    { char: "吵", tone: 3 },
    { char: "炒", tone: 3 },
  ],
  che: [
    { char: "车", tone: 1 },
    { char: "扯", tone: 3 },
    { char: "彻", tone: 4 },
    { char: "撤", tone: 4 },
    { char: "澈", tone: 4 },
  ],
  chen: [
    { char: "臣", tone: 2 },
    { char: "沉", tone: 2 },
    { char: "辰", tone: 2 },
    { char: "晨", tone: 2 },
    { char: "陈", tone: 2 },
    { char: "尘", tone: 2 },
    { char: "趁", tone: 4 },
    { char: "衬", tone: 4 },
  ],
  cheng: [
    { char: "称", tone: 1 },
    { char: "撑", tone: 1 },
    { char: "成", tone: 2 },
    { char: "城", tone: 2 },
    { char: "程", tone: 2 },
    { char: "乘", tone: 2 },
    { char: "呈", tone: 2 },
    { char: "承", tone: 2 },
    { char: "逞", tone: 3 },
    { char: "秤", tone: 4 },
    { char: "称", tone: 4 },
  ],
  chi: [
    { char: "吃", tone: 1 },
    { char: "痴", tone: 1 },
    { char: "持", tone: 2 },
    { char: "池", tone: 2 },
    { char: "迟", tone: 2 },
    { char: "尺", tone: 3 },
    { char: "齿", tone: 3 },
    { char: "赤", tone: 4 },
    { char: "翅", tone: 4 },
    { char: "斥", tone: 4 },
  ],
  chong: [
    { char: "充", tone: 1 },
    { char: "冲", tone: 1 },
    { char: "虫", tone: 2 },
    { char: "崇", tone: 2 },
    { char: "宠", tone: 3 },
    { char: "冲", tone: 4 },
  ],
  chou: [
    { char: "抽", tone: 1 },
    { char: "仇", tone: 2 },
    { char: "愁", tone: 2 },
    { char: "稠", tone: 2 },
    { char: "筹", tone: 2 },
    { char: "丑", tone: 3 },
    { char: "臭", tone: 4 },
  ],
  chu: [
    { char: "出", tone: 1 },
    { char: "初", tone: 1 },
    { char: "除", tone: 2 },
    { char: "厨", tone: 2 },
    { char: "锄", tone: 2 },
    { char: "处", tone: 3 },
    { char: "楚", tone: 3 },
    { char: "础", tone: 3 },
    { char: "处", tone: 4 },
    { char: "触", tone: 4 },
    { char: "畜", tone: 4 },
  ],
  chuai: [
    { char: "揣", tone: 1 },
    { char: "揣", tone: 3 },
    { char: "踹", tone: 4 },
  ],
  chuan: [
    { char: "川", tone: 1 },
    { char: "穿", tone: 1 },
    { char: "传", tone: 2 },
    { char: "船", tone: 2 },
    { char: "串", tone: 4 },
  ],
  chuang: [
    { char: "窗", tone: 1 },
    { char: "疮", tone: 1 },
    { char: "床", tone: 2 },
    { char: "闯", tone: 3 },
    { char: "创", tone: 4 },
  ],
  chui: [
    { char: "吹", tone: 1 },
    { char: "垂", tone: 2 },
    { char: "锤", tone: 2 },
    { char: "捶", tone: 2 },
  ],
  chun: [
    { char: "春", tone: 1 },
    { char: "纯", tone: 2 },
    { char: "唇", tone: 2 },
    { char: "醇", tone: 2 },
    { char: "蠢", tone: 3 },
  ],
  chuo: [
    { char: "戳", tone: 1 },
    { char: "绰", tone: 4 },
  ],
  ci: [
    { char: "词", tone: 2 },
    { char: "辞", tone: 2 },
    { char: "慈", tone: 2 },
    { char: "磁", tone: 2 },
    { char: "此", tone: 3 },
    { char: "次", tone: 4 },
    { char: "刺", tone: 4 },
    { char: "赐", tone: 4 },
  ],
  cong: [
    { char: "从", tone: 2 },
    { char: "丛", tone: 2 },
    { char: "聪", tone: 1 },
    { char: "从", tone: 2 },
    { char: "丛", tone: 2 },
  ],
  cou: [{ char: "凑", tone: 4 }],
  cu: [
    { char: "粗", tone: 1 },
    { char: "促", tone: 4 },
    { char: "醋", tone: 4 },
  ],
  cuan: [
    { char: "蹿", tone: 1 },
    { char: "攒", tone: 2 },
    { char: "窜", tone: 4 },
  ],
  cui: [
    { char: "催", tone: 1 },
    { char: "崔", tone: 1 },
    { char: "脆", tone: 4 },
    { char: "翠", tone: 4 },
    { char: "粹", tone: 4 },
  ],
  cun: [
    { char: "村", tone: 1 },
    { char: "存", tone: 2 },
    { char: "寸", tone: 4 },
  ],
  cuo: [
    { char: "搓", tone: 1 },
    { char: "磋", tone: 1 },
    { char: "错", tone: 4 },
    { char: "措", tone: 4 },
    { char: "挫", tone: 4 },
  ],
  da: [
    { char: "搭", tone: 1 },
    { char: "达", tone: 2 },
    { char: "答", tone: 2 },
    { char: "打", tone: 2 },
    { char: "打", tone: 3 },
    { char: "大", tone: 4 },
  ],
  dai: [
    { char: "呆", tone: 1 },
    { char: "待", tone: 4 },
    { char: "代", tone: 4 },
    { char: "带", tone: 4 },
    { char: "戴", tone: 4 },
    { char: "袋", tone: 4 },
    { char: "贷", tone: 4 },
    { char: "怠", tone: 4 },
  ],
  dan: [
    { char: "担", tone: 1 },
    { char: "单", tone: 1 },
    { char: "耽", tone: 1 },
    { char: "胆", tone: 3 },
    { char: "但", tone: 4 },
    { char: "蛋", tone: 4 },
    { char: "淡", tone: 4 },
    { char: "弹", tone: 4 },
    { char: "旦", tone: 4 },
    { char: "担", tone: 4 },
  ],
  dang: [
    { char: "当", tone: 1 },
    { char: "铛", tone: 1 },
    { char: "挡", tone: 3 },
    { char: "党", tone: 3 },
    { char: "当", tone: 4 },
    { char: "荡", tone: 4 },
    { char: "档", tone: 4 },
  ],
  dao: [
    { char: "刀", tone: 1 },
    { char: "岛", tone: 3 },
    { char: "导", tone: 3 },
    { char: "倒", tone: 3 },
    { char: "蹈", tone: 3 },
    { char: "到", tone: 4 },
    { char: "道", tone: 4 },
    { char: "倒", tone: 4 },
    { char: "盗", tone: 4 },
    { char: "稻", tone: 4 },
  ],
  de: [
    { char: "的", tone: 5 },
    { char: "得", tone: 2 },
    { char: "德", tone: 2 },
    { char: "地", tone: 5 },
  ],
  dei: [{ char: "得", tone: 3 }],
  den: [{ char: "扥", tone: 4 }],
  deng: [
    { char: "灯", tone: 1 },
    { char: "登", tone: 1 },
    { char: "等", tone: 3 },
    { char: "邓", tone: 4 },
    { char: "凳", tone: 4 },
    { char: "瞪", tone: 4 },
  ],
  di: [
    { char: "低", tone: 1 },
    { char: "滴", tone: 1 },
    { char: "敌", tone: 2 },
    { char: "迪", tone: 2 },
    { char: "笛", tone: 2 },
    { char: "底", tone: 3 },
    { char: "抵", tone: 3 },
    { char: "地", tone: 4 },
    { char: "第", tone: 4 },
    { char: "帝", tone: 4 },
    { char: "弟", tone: 4 },
    { char: "递", tone: 4 },
    { char: "蒂", tone: 4 },
  ],
  dia: [{ char: "嗲", tone: 3 }],
  dian: [
    { char: "颠", tone: 1 },
    { char: "掂", tone: 1 },
    { char: "点", tone: 3 },
    { char: "典", tone: 3 },
    { char: "电", tone: 4 },
    { char: "店", tone: 4 },
    { char: "垫", tone: 4 },
    { char: "殿", tone: 4 },
    { char: "奠", tone: 4 },
  ],
  diao: [
    { char: "雕", tone: 1 },
    { char: "凋", tone: 1 },
    { char: "吊", tone: 4 },
    { char: "掉", tone: 4 },
    { char: "调", tone: 4 },
  ],
  die: [
    { char: "爹", tone: 1 },
    { char: "叠", tone: 2 },
    { char: "碟", tone: 2 },
    { char: "蝶", tone: 2 },
  ],
  ding: [
    { char: "丁", tone: 1 },
    { char: "盯", tone: 1 },
    { char: "钉", tone: 1 },
    { char: "顶", tone: 3 },
    { char: "鼎", tone: 3 },
    { char: "定", tone: 4 },
    { char: "订", tone: 4 },
    { char: "钉", tone: 4 },
  ],
  diu: [{ char: "丢", tone: 1 }],
  dong: [
    { char: "东", tone: 1 },
    { char: "冬", tone: 1 },
    { char: "懂", tone: 3 },
    { char: "董", tone: 3 },
    { char: "动", tone: 4 },
    { char: "洞", tone: 4 },
    { char: "冻", tone: 4 },
    { char: "栋", tone: 4 },
  ],
  dou: [
    { char: "都", tone: 1 },
    { char: "兜", tone: 1 },
    { char: "斗", tone: 3 },
    { char: "抖", tone: 3 },
    { char: "陡", tone: 3 },
    { char: "豆", tone: 4 },
    { char: "斗", tone: 4 },
    { char: "逗", tone: 4 },
    { char: "痘", tone: 4 },
  ],
  du: [
    { char: "都", tone: 1 },
    { char: "督", tone: 1 },
    { char: "读", tone: 2 },
    { char: "独", tone: 2 },
    { char: "毒", tone: 2 },
    { char: "赌", tone: 3 },
    { char: "堵", tone: 3 },
    { char: "度", tone: 4 },
    { char: "杜", tone: 4 },
    { char: "肚", tone: 4 },
    { char: "渡", tone: 4 },
    { char: "妒", tone: 4 },
  ],
  duan: [
    { char: "端", tone: 1 },
    { char: "短", tone: 3 },
    { char: "段", tone: 4 },
    { char: "断", tone: 4 },
    { char: "锻", tone: 4 },
  ],
  dui: [
    { char: "堆", tone: 1 },
    { char: "对", tone: 4 },
    { char: "队", tone: 4 },
    { char: "兑", tone: 4 },
  ],
  dun: [
    { char: "吨", tone: 1 },
    { char: "蹲", tone: 1 },
    { char: "顿", tone: 4 },
    { char: "盾", tone: 4 },
    { char: "钝", tone: 4 },
  ],
  duo: [
    { char: "多", tone: 1 },
    { char: "哆", tone: 1 },
    { char: "夺", tone: 2 },
    { char: "朵", tone: 3 },
    { char: "躲", tone: 3 },
    { char: "舵", tone: 4 },
    { char: "堕", tone: 4 },
    { char: "惰", tone: 4 },
  ],
  e: [
    { char: "阿", tone: 1 },
    { char: "婀", tone: 1 },
    { char: "鹅", tone: 2 },
    { char: "额", tone: 2 },
    { char: "俄", tone: 2 },
    { char: "恶", tone: 3 },
    { char: "饿", tone: 4 },
    { char: "恶", tone: 4 },
    { char: "遏", tone: 4 },
    { char: "厄", tone: 4 },
  ],
  ei: [
    { char: "诶", tone: 1 },
    { char: "诶", tone: 2 },
    { char: "诶", tone: 3 },
    { char: "诶", tone: 4 },
  ],
  en: [
    { char: "恩", tone: 1 },
    { char: "摁", tone: 4 },
  ],
  er: [
    { char: "儿", tone: 2 },
    { char: "而", tone: 2 },
    { char: "耳", tone: 3 },
    { char: "尔", tone: 3 },
    { char: "二", tone: 4 },
  ],
  fa: [
    { char: "发", tone: 1 },
    { char: "罚", tone: 2 },
    { char: "乏", tone: 2 },
    { char: "伐", tone: 2 },
    { char: "法", tone: 3 },
    { char: "发", tone: 4 },
  ],
  fan: [
    { char: "翻", tone: 1 },
    { char: "帆", tone: 1 },
    { char: "凡", tone: 2 },
    { char: "烦", tone: 2 },
    { char: "繁", tone: 2 },
    { char: "反", tone: 3 },
    { char: "返", tone: 3 },
    { char: "饭", tone: 4 },
    { char: "范", tone: 4 },
    { char: "犯", tone: 4 },
    { char: "泛", tone: 4 },
  ],
  fang: [
    { char: "方", tone: 1 },
    { char: "芳", tone: 1 },
    { char: "房", tone: 2 },
    { char: "防", tone: 2 },
    { char: "妨", tone: 2 },
    { char: "仿", tone: 3 },
    { char: "访", tone: 3 },
    { char: "纺", tone: 3 },
    { char: "放", tone: 4 },
  ],
  fei: [
    { char: "飞", tone: 1 },
    { char: "非", tone: 1 },
    { char: "肥", tone: 2 },
    { char: "匪", tone: 3 },
    { char: "费", tone: 4 },
    { char: "废", tone: 4 },
    { char: "肺", tone: 4 },
    { char: "沸", tone: 4 },
  ],
  fen: [
    { char: "分", tone: 1 },
    { char: "纷", tone: 1 },
    { char: "芬", tone: 1 },
    { char: "焚", tone: 2 },
    { char: "粉", tone: 3 },
    { char: "分", tone: 4 },
    { char: "份", tone: 4 },
    { char: "奋", tone: 4 },
    { char: "愤", tone: 4 },
  ],
  feng: [
    { char: "风", tone: 1 },
    { char: "丰", tone: 1 },
    { char: "封", tone: 1 },
    { char: "峰", tone: 1 },
    { char: "锋", tone: 1 },
    { char: "疯", tone: 1 },
    { char: "冯", tone: 2 },
    { char: "逢", tone: 2 },
    { char: "凤", tone: 4 },
    { char: "奉", tone: 4 },
    { char: "缝", tone: 4 },
  ],
  fo: [{ char: "佛", tone: 2 }],
  fou: [{ char: "否", tone: 3 }],
  fu: [
    { char: "夫", tone: 1 },
    { char: "肤", tone: 1 },
    { char: "孵", tone: 1 },
    { char: "扶", tone: 2 },
    { char: "福", tone: 2 },
    { char: "服", tone: 2 },
    { char: "浮", tone: 2 },
    { char: "符", tone: 2 },
    { char: "幅", tone: 2 },
    { char: "府", tone: 3 },
    { char: "辅", tone: 3 },
    { char: "抚", tone: 3 },
    { char: "腐", tone: 3 },
    { char: "父", tone: 4 },
    { char: "付", tone: 4 },
    { char: "负", tone: 4 },
    { char: "妇", tone: 4 },
    { char: "附", tone: 4 },
    { char: "复", tone: 4 },
    { char: "富", tone: 4 },
    { char: "副", tone: 4 },
    { char: "覆", tone: 4 },
  ],
  ga: [
    { char: "嘎", tone: 1 },
    { char: "尬", tone: 4 },
  ],
  gai: [
    { char: "该", tone: 1 },
    { char: "改", tone: 3 },
    { char: "盖", tone: 4 },
    { char: "概", tone: 4 },
    { char: "钙", tone: 4 },
  ],
  gan: [
    { char: "干", tone: 1 },
    { char: "甘", tone: 1 },
    { char: "肝", tone: 1 },
    { char: "竿", tone: 1 },
    { char: "感", tone: 3 },
    { char: "赶", tone: 3 },
    { char: "敢", tone: 3 },
    { char: "干", tone: 4 },
    { char: "赣", tone: 4 },
  ],
  gang: [
    { char: "刚", tone: 1 },
    { char: "钢", tone: 1 },
    { char: "纲", tone: 1 },
    { char: "缸", tone: 1 },
    { char: "港", tone: 3 },
    { char: "杠", tone: 4 },
  ],
  gao: [
    { char: "高", tone: 1 },
    { char: "糕", tone: 1 },
    { char: "膏", tone: 1 },
    { char: "搞", tone: 3 },
    { char: "稿", tone: 3 },
    { char: "告", tone: 4 },
  ],
  ge: [
    { char: "歌", tone: 1 },
    { char: "哥", tone: 1 },
    { char: "割", tone: 1 },
    { char: "搁", tone: 1 },
    { char: "格", tone: 2 },
    { char: "革", tone: 2 },
    { char: "隔", tone: 2 },
    { char: "阁", tone: 2 },
    { char: "个", tone: 4 },
    { char: "各", tone: 4 },
    { char: "个", tone: 5 },
  ],
  gei: [{ char: "给", tone: 3 }],
  gen: [
    { char: "根", tone: 1 },
    { char: "跟", tone: 1 },
    { char: "哏", tone: 2 },
  ],
  geng: [
    { char: "耕", tone: 1 },
    { char: "更", tone: 1 },
    { char: "耿", tone: 3 },
    { char: "更", tone: 4 },
  ],
  gong: [
    { char: "工", tone: 1 },
    { char: "公", tone: 1 },
    { char: "功", tone: 1 },
    { char: "供", tone: 1 },
    { char: "宫", tone: 1 },
    { char: "弓", tone: 1 },
    { char: "共", tone: 4 },
    { char: "贡", tone: 4 },
    { char: "供", tone: 4 },
  ],
  gou: [
    { char: "沟", tone: 1 },
    { char: "钩", tone: 1 },
    { char: "勾", tone: 1 },
    { char: "狗", tone: 3 },
    { char: "够", tone: 4 },
    { char: "构", tone: 4 },
    { char: "购", tone: 4 },
  ],
  gu: [
    { char: "姑", tone: 1 },
    { char: "孤", tone: 1 },
    { char: "估", tone: 1 },
    { char: "咕", tone: 1 },
    { char: "古", tone: 3 },
    { char: "鼓", tone: 3 },
    { char: "股", tone: 3 },
    { char: "骨", tone: 3 },
    { char: "谷", tone: 3 },
    { char: "故", tone: 4 },
    { char: "顾", tone: 4 },
    { char: "固", tone: 4 },
    { char: "雇", tone: 4 },
  ],
  gua: [
    { char: "瓜", tone: 1 },
    { char: "刮", tone: 1 },
    { char: "寡", tone: 3 },
    { char: "挂", tone: 4 },
    { char: "卦", tone: 4 },
  ],
  guai: [
    { char: "乖", tone: 1 },
    { char: "拐", tone: 3 },
    { char: "怪", tone: 4 },
  ],
  guan: [
    { char: "关", tone: 1 },
    { char: "观", tone: 1 },
    { char: "官", tone: 1 },
    { char: "冠", tone: 1 },
    { char: "管", tone: 3 },
    { char: "馆", tone: 3 },
    { char: "惯", tone: 4 },
    { char: "灌", tone: 4 },
    { char: "罐", tone: 4 },
    { char: "观", tone: 4 },
  ],
  guang: [
    { char: "光", tone: 1 },
    { char: "广", tone: 3 },
    { char: "逛", tone: 4 },
  ],
  gui: [
    { char: "归", tone: 1 },
    { char: "规", tone: 1 },
    { char: "龟", tone: 1 },
    { char: "硅", tone: 1 },
    { char: "鬼", tone: 3 },
    { char: "轨", tone: 3 },
    { char: "诡", tone: 3 },
    { char: "贵", tone: 4 },
    { char: "桂", tone: 4 },
    { char: "柜", tone: 4 },
    { char: "跪", tone: 4 },
  ],
  gun: [
    { char: "滚", tone: 3 },
    { char: "棍", tone: 4 },
  ],
  guo: [
    { char: "锅", tone: 1 },
    { char: "郭", tone: 1 },
    { char: "国", tone: 2 },
    { char: "果", tone: 3 },
    { char: "裹", tone: 3 },
    { char: "过", tone: 4 },
  ],
  ha: [
    { char: "哈", tone: 1 },
    { char: "哈", tone: 2 }, // 哈蟆
    { char: "哈", tone: 3 },
    { char: "哈", tone: 4 },
  ],
  hai: [
    { char: "还", tone: 2 },
    { char: "孩", tone: 2 },
    { char: "海", tone: 3 },
    { char: "害", tone: 4 },
    { char: "骇", tone: 4 },
  ],
  han: [
    { char: "憨", tone: 1 },
    { char: "寒", tone: 2 },
    { char: "韩", tone: 2 },
    { char: "含", tone: 2 },
    { char: "函", tone: 2 },
    { char: "喊", tone: 3 },
    { char: "汉", tone: 4 },
    { char: "汗", tone: 4 },
    { char: "旱", tone: 4 },
    { char: "憾", tone: 4 },
  ],
  hang: [
    { char: "行", tone: 2 },
    { char: "航", tone: 2 },
  ],
  hao: [
    { char: "毫", tone: 2 },
    { char: "豪", tone: 2 },
    { char: "好", tone: 3 },
    { char: "号", tone: 4 },
    { char: "好", tone: 4 },
    { char: "耗", tone: 4 },
  ],
  he: [
    { char: "喝", tone: 1 },
    { char: "呵", tone: 1 },
    { char: "和", tone: 2 },
    { char: "河", tone: 2 },
    { char: "核", tone: 2 },
    { char: "何", tone: 2 },
    { char: "合", tone: 2 },
    { char: "盒", tone: 2 },
    { char: "和", tone: 4 },
    { char: "贺", tone: 4 },
    { char: "荷", tone: 4 },
    { char: "和", tone: 5 },
  ],
  hei: [{ char: "黑", tone: 1 }],
  hen: [
    { char: "很", tone: 3 },
    { char: "狠", tone: 3 },
    { char: "恨", tone: 4 },
  ],
  heng: [
    { char: "哼", tone: 1 },
    { char: "横", tone: 2 },
    { char: "恒", tone: 2 },
    { char: "横", tone: 4 },
  ],
  hong: [
    { char: "红", tone: 2 },
    { char: "宏", tone: 2 },
    { char: "洪", tone: 2 },
    { char: "虹", tone: 2 },
    { char: "哄", tone: 3 },
    { char: "哄", tone: 4 },
  ],
  hou: [
    { char: "猴", tone: 2 },
    { char: "喉", tone: 2 },
    { char: "吼", tone: 3 },
    { char: "后", tone: 4 },
    { char: "厚", tone: 4 },
    { char: "候", tone: 4 },
  ],
  hu: [
    { char: "呼", tone: 1 },
    { char: "忽", tone: 1 },
    { char: "胡", tone: 2 },
    { char: "湖", tone: 2 },
    { char: "糊", tone: 2 },
    { char: "狐", tone: 2 },
    { char: "壶", tone: 2 },
    { char: "虎", tone: 3 },
    { char: "互", tone: 4 },
    { char: "户", tone: 4 },
    { char: "护", tone: 4 },
    { char: "糊", tone: 4 },
  ],
  hua: [
    { char: "花", tone: 1 },
    { char: "华", tone: 2 },
    { char: "划", tone: 2 },
    { char: "滑", tone: 2 },
    { char: "化", tone: 4 },
    { char: "话", tone: 4 },
    { char: "画", tone: 4 },
    { char: "划", tone: 4 },
    { char: "桦", tone: 4 },
  ],
  huai: [
    { char: "怀", tone: 2 },
    { char: "淮", tone: 2 },
    { char: "坏", tone: 4 },
  ],
  huan: [
    { char: "欢", tone: 1 },
    { char: "环", tone: 2 },
    { char: "还", tone: 2 },
    { char: "缓", tone: 3 },
    { char: "换", tone: 4 },
    { char: "唤", tone: 4 },
    { char: "患", tone: 4 },
    { char: "幻", tone: 4 },
  ],
  huang: [
    { char: "荒", tone: 1 },
    { char: "慌", tone: 1 },
    { char: "黄", tone: 2 },
    { char: "皇", tone: 2 },
    { char: "煌", tone: 2 },
    { char: "惶", tone: 2 },
    { char: "谎", tone: 3 },
    { char: "晃", tone: 4 },
  ],
  hui: [
    { char: "灰", tone: 1 },
    { char: "挥", tone: 1 },
    { char: "辉", tone: 1 },
    { char: "恢", tone: 1 },
    { char: "回", tone: 2 },
    { char: "毁", tone: 3 },
    { char: "悔", tone: 3 },
    { char: "会", tone: 4 },
    { char: "汇", tone: 4 },
    { char: "慧", tone: 4 },
    { char: "惠", tone: 4 },
    { char: "绘", tone: 4 },
  ],
  hun: [
    { char: "婚", tone: 1 },
    { char: "昏", tone: 1 },
    { char: "魂", tone: 2 },
    { char: "混", tone: 4 },
  ],
  huo: [
    { char: "活", tone: 2 },
    { char: "火", tone: 3 },
    { char: "伙", tone: 3 },
    { char: "或", tone: 4 },
    { char: "货", tone: 4 },
    { char: "获", tone: 4 },
    { char: "祸", tone: 4 },
    { char: "霍", tone: 4 },
  ],
  ji: [
    { char: "几", tone: 1 },
    { char: "机", tone: 1 },
    { char: "鸡", tone: 1 },
    { char: "基", tone: 1 },
    { char: "激", tone: 1 },
    { char: "积", tone: 1 },
    { char: "击", tone: 1 },
    { char: "及", tone: 2 },
    { char: "级", tone: 2 },
    { char: "极", tone: 2 },
    { char: "吉", tone: 2 },
    { char: "即", tone: 2 },
    { char: "急", tone: 2 },
    { char: "几", tone: 3 },
    { char: "己", tone: 3 },
    { char: "挤", tone: 3 },
    { char: "计", tone: 4 },
    { char: "记", tone: 4 },
    { char: "技", tone: 4 },
    { char: "季", tone: 4 },
    { char: "既", tone: 4 },
    { char: "继", tone: 4 },
    { char: "寄", tone: 4 },
    { char: "际", tone: 4 },
  ],
  jia: [
    { char: "家", tone: 1 },
    { char: "加", tone: 1 },
    { char: "佳", tone: 1 },
    { char: "嘉", tone: 1 },
    { char: "甲", tone: 3 },
    { char: "假", tone: 3 },
    { char: "价", tone: 4 },
    { char: "架", tone: 4 },
    { char: "驾", tone: 4 },
    { char: "嫁", tone: 4 },
  ],
  jian: [
    { char: "间", tone: 1 },
    { char: "坚", tone: 1 },
    { char: "尖", tone: 1 },
    { char: "兼", tone: 1 },
    { char: "监", tone: 1 },
    { char: "简", tone: 3 },
    { char: "减", tone: 3 },
    { char: "检", tone: 3 },
    { char: "剪", tone: 3 },
    { char: "捡", tone: 3 },
    { char: "见", tone: 4 },
    { char: "件", tone: 4 },
    { char: "建", tone: 4 },
    { char: "健", tone: 4 },
    { char: "剑", tone: 4 },
    { char: "渐", tone: 4 },
    { char: "键", tone: 4 },
  ],
  jiang: [
    { char: "将", tone: 1 },
    { char: "江", tone: 1 },
    { char: "姜", tone: 1 },
    { char: "浆", tone: 1 },
    { char: "讲", tone: 3 },
    { char: "奖", tone: 3 },
    { char: "将", tone: 4 },
    { char: "匠", tone: 4 },
    { char: "降", tone: 4 },
    { char: "酱", tone: 4 },
  ],
  jiao: [
    { char: "交", tone: 1 },
    { char: "教", tone: 1 },
    { char: "焦", tone: 1 },
    { char: "胶", tone: 1 },
    { char: "娇", tone: 1 },
    { char: "角", tone: 3 },
    { char: "脚", tone: 3 },
    { char: "搅", tone: 3 },
    { char: "叫", tone: 4 },
    { char: "较", tone: 4 },
    { char: "觉", tone: 4 },
    { char: "轿", tone: 4 },
    { char: "教", tone: 4 },
  ],
  jie: [
    { char: "接", tone: 1 },
    { char: "街", tone: 1 },
    { char: "阶", tone: 1 },
    { char: "节", tone: 2 },
    { char: "结", tone: 2 },
    { char: "洁", tone: 2 },
    { char: "杰", tone: 2 },
    { char: "姐", tone: 3 },
    { char: "解", tone: 3 },
    { char: "界", tone: 4 },
    { char: "借", tone: 4 },
    { char: "介", tone: 4 },
    { char: "戒", tone: 4 },
  ],
  jin: [
    { char: "今", tone: 1 },
    { char: "金", tone: 1 },
    { char: "巾", tone: 1 },
    { char: "斤", tone: 1 },
    { char: "筋", tone: 1 },
    { char: "仅", tone: 3 },
    { char: "紧", tone: 3 },
    { char: "锦", tone: 3 },
    { char: "进", tone: 4 },
    { char: "近", tone: 4 },
    { char: "尽", tone: 4 },
    { char: "劲", tone: 4 },
    { char: "禁", tone: 4 },
  ],
  jing: [
    { char: "经", tone: 1 },
    { char: "精", tone: 1 },
    { char: "京", tone: 1 },
    { char: "惊", tone: 1 },
    { char: "睛", tone: 1 },
    { char: "景", tone: 3 },
    { char: "警", tone: 3 },
    { char: "井", tone: 3 },
    { char: "竟", tone: 4 },
    { char: "静", tone: 4 },
    { char: "敬", tone: 4 },
    { char: "镜", tone: 4 },
    { char: "境", tone: 4 },
  ],
  jiong: [{ char: "窘", tone: 3 }],
  jiu: [
    { char: "纠", tone: 1 },
    { char: "究", tone: 1 },
    { char: "九", tone: 3 },
    { char: "久", tone: 3 },
    { char: "酒", tone: 3 },
    { char: "就", tone: 4 },
    { char: "旧", tone: 4 },
    { char: "救", tone: 4 },
    { char: "舅", tone: 4 },
  ],
  ju: [
    { char: "居", tone: 1 },
    { char: "拘", tone: 1 },
    { char: "局", tone: 2 },
    { char: "菊", tone: 2 },
    { char: "举", tone: 3 },
    { char: "矩", tone: 3 },
    { char: "句", tone: 4 },
    { char: "具", tone: 4 },
    { char: "据", tone: 4 },
    { char: "巨", tone: 4 },
    { char: "拒", tone: 4 },
    { char: "聚", tone: 4 },
  ],
  juan: [
    { char: "捐", tone: 1 },
    { char: "卷", tone: 3 },
    { char: "卷", tone: 4 },
    { char: "倦", tone: 4 },
  ],
  jue: [
    { char: "决", tone: 2 },
    { char: "觉", tone: 2 },
    { char: "绝", tone: 2 },
    { char: "角", tone: 2 },
    { char: "倔", tone: 4 },
  ],
  jun: [
    { char: "军", tone: 1 },
    { char: "均", tone: 1 },
    { char: "君", tone: 1 },
    { char: "俊", tone: 4 },
    { char: "峻", tone: 4 },
  ],
  ka: [
    { char: "咖", tone: 1 },
    { char: "喀", tone: 1 },
    { char: "卡", tone: 3 },
  ],
  kai: [
    { char: "开", tone: 1 },
    { char: "凯", tone: 3 },
    { char: "楷", tone: 3 },
  ],
  kan: [
    { char: "刊", tone: 1 },
    { char: "堪", tone: 1 },
    { char: "砍", tone: 3 },
    { char: "看", tone: 4 },
  ],
  kang: [
    { char: "康", tone: 1 },
    { char: "抗", tone: 4 },
    { char: "炕", tone: 4 },
  ],
  kao: [
    { char: "考", tone: 3 },
    { char: "烤", tone: 3 },
    { char: "靠", tone: 4 },
  ],
  ke: [
    { char: "科", tone: 1 },
    { char: "棵", tone: 1 },
    { char: "颗", tone: 1 },
    { char: "壳", tone: 1 },
    { char: "咳", tone: 2 },
    { char: "壳", tone: 2 },
    { char: "可", tone: 3 },
    { char: "渴", tone: 3 },
    { char: "课", tone: 4 },
    { char: "客", tone: 4 },
    { char: "刻", tone: 4 },
    { char: "克", tone: 4 },
  ],
  ken: [
    { char: "肯", tone: 3 },
    { char: "恳", tone: 3 },
  ],
  keng: [{ char: "坑", tone: 1 }],
  kong: [
    { char: "空", tone: 1 },
    { char: "孔", tone: 3 },
    { char: "恐", tone: 3 },
    { char: "空", tone: 4 },
    { char: "控", tone: 4 },
  ],
  kou: [
    { char: "口", tone: 3 },
    { char: "扣", tone: 4 },
    { char: "寇", tone: 4 },
  ],
  ku: [
    { char: "哭", tone: 1 },
    { char: "苦", tone: 3 },
    { char: "库", tone: 4 },
    { char: "裤", tone: 4 },
    { char: "酷", tone: 4 },
  ],
  kua: [
    { char: "夸", tone: 1 },
    { char: "跨", tone: 4 },
  ],
  kuai: [
    { char: "快", tone: 4 },
    { char: "块", tone: 4 },
    { char: "筷", tone: 4 },
  ],
  kuan: [
    { char: "宽", tone: 1 },
    { char: "款", tone: 3 },
  ],
  kuang: [
    { char: "框", tone: 1 },
    { char: "狂", tone: 2 },
    { char: "况", tone: 4 },
    { char: "矿", tone: 4 },
    { char: "框", tone: 4 },
  ],
  kui: [
    { char: "亏", tone: 1 },
    { char: "葵", tone: 2 },
    { char: "愧", tone: 4 },
    { char: "溃", tone: 4 },
  ],
  kun: [
    { char: "昆", tone: 1 },
    { char: "捆", tone: 3 },
    { char: "困", tone: 4 },
  ],
  kuo: [
    { char: "扩", tone: 4 },
    { char: "阔", tone: 4 },
  ],
  la: [
    { char: "拉", tone: 1 },
    { char: "垃", tone: 1 },
    { char: "喇", tone: 3 },
    { char: "辣", tone: 4 },
    { char: "腊", tone: 4 },
  ],
  lai: [
    { char: "来", tone: 2 },
    { char: "赖", tone: 4 },
  ],
  lan: [
    { char: "兰", tone: 2 },
    { char: "蓝", tone: 2 },
    { char: "拦", tone: 2 },
    { char: "懒", tone: 3 },
    { char: "烂", tone: 4 },
    { char: "滥", tone: 4 },
  ],
  lang: [
    { char: "狼", tone: 2 },
    { char: "郎", tone: 2 },
    { char: "朗", tone: 3 },
    { char: "浪", tone: 4 },
  ],
  lao: [
    { char: "捞", tone: 1 },
    { char: "劳", tone: 2 },
    { char: "牢", tone: 2 },
    { char: "老", tone: 3 },
    { char: "烙", tone: 4 },
    { char: "涝", tone: 4 },
  ],
  le: [
    { char: "了", tone: 5 }, // neutral, treated as tone 5
    { char: "乐", tone: 4 },
  ],
  lei: [
    { char: "雷", tone: 2 },
    { char: "累", tone: 3 },
    { char: "类", tone: 4 },
    { char: "泪", tone: 4 },
    { char: "累", tone: 4 },
  ],
  leng: [{ char: "冷", tone: 3 }],
  li: [
    { char: "离", tone: 2 },
    { char: "梨", tone: 2 },
    { char: "璃", tone: 2 },
    { char: "里", tone: 3 },
    { char: "理", tone: 3 },
    { char: "礼", tone: 3 },
    { char: "力", tone: 4 },
    { char: "立", tone: 4 },
    { char: "利", tone: 4 },
    { char: "例", tone: 4 },
    { char: "丽", tone: 4 },
    { char: "历", tone: 4 },
  ],
  lia: [{ char: "俩", tone: 3 }],
  lian: [
    { char: "连", tone: 2 },
    { char: "联", tone: 2 },
    { char: "莲", tone: 2 },
    { char: "脸", tone: 3 },
    { char: "练", tone: 4 },
    { char: "炼", tone: 4 },
    { char: "恋", tone: 4 },
  ],
  liang: [
    { char: "凉", tone: 2 },
    { char: "良", tone: 2 },
    { char: "粮", tone: 2 },
    { char: "两", tone: 3 },
    { char: "辆", tone: 3 },
    { char: "亮", tone: 4 },
    { char: "谅", tone: 4 },
  ],
  liao: [
    { char: "辽", tone: 2 },
    { char: "疗", tone: 2 },
    { char: "了", tone: 3 },
    { char: "料", tone: 4 },
  ],
  lie: [
    { char: "列", tone: 4 },
    { char: "烈", tone: 4 },
    { char: "裂", tone: 4 },
    { char: "劣", tone: 4 },
  ],
  lin: [
    { char: "林", tone: 2 },
    { char: "临", tone: 2 },
    { char: "邻", tone: 2 },
    { char: "淋", tone: 4 },
  ],
  ling: [
    { char: "零", tone: 2 },
    { char: "灵", tone: 2 },
    { char: "铃", tone: 2 },
    { char: "龄", tone: 2 },
    { char: "领", tone: 3 },
    { char: "岭", tone: 3 },
    { char: "另", tone: 4 },
    { char: "令", tone: 4 },
  ],
  liu: [
    { char: "流", tone: 2 },
    { char: "留", tone: 2 },
    { char: "刘", tone: 2 },
    { char: "六", tone: 4 },
  ],
  long: [
    { char: "龙", tone: 2 },
    { char: "笼", tone: 2 },
    { char: "拢", tone: 3 },
    { char: "弄", tone: 4 },
  ],
  lou: [
    { char: "楼", tone: 2 },
    { char: "漏", tone: 4 },
  ],
  lu: [
    { char: "录", tone: 4 },
    { char: "路", tone: 4 },
    { char: "陆", tone: 4 },
    { char: "露", tone: 4 },
    { char: "鹿", tone: 4 },
    { char: "鲁", tone: 3 },
  ],
  lv: [
    { char: "绿", tone: 4 },
    { char: "率", tone: 4 },
    { char: "律", tone: 4 },
    { char: "旅", tone: 3 },
    { char: "吕", tone: 3 },
  ],
  luan: [
    { char: "乱", tone: 4 },
    { char: "卵", tone: 3 },
  ],
  lue: [
    { char: "略", tone: 4 },
    { char: "掠", tone: 4 },
  ],
  lun: [
    { char: "论", tone: 4 },
    { char: "轮", tone: 2 },
  ],
  luo: [
    { char: "罗", tone: 2 },
    { char: "落", tone: 4 },
    { char: "骆", tone: 4 },
    { char: "裸", tone: 3 },
  ],
  ma: [
    { char: "妈", tone: 1 },
    { char: "吗", tone: 1 },
    { char: "麻", tone: 2 },
    { char: "马", tone: 3 },
    { char: "码", tone: 3 },
    { char: "骂", tone: 4 },
    { char: "吗", tone: 5 },
  ],
  mai: [
    { char: "买", tone: 3 },
    { char: "卖", tone: 4 },
    { char: "迈", tone: 4 },
    { char: "麦", tone: 4 },
  ],
  man: [
    { char: "满", tone: 3 },
    { char: "慢", tone: 4 },
    { char: "漫", tone: 4 },
  ],
  mang: [
    { char: "忙", tone: 2 },
    { char: "盲", tone: 2 },
  ],
  mao: [
    { char: "猫", tone: 1 },
    { char: "毛", tone: 2 },
    { char: "矛", tone: 2 },
    { char: "冒", tone: 4 },
    { char: "帽", tone: 4 },
    { char: "贸", tone: 4 },
  ],
  me: [
    { char: "么", tone: 5 }, // 什么
  ],
  mei: [
    { char: "没", tone: 2 },
    { char: "梅", tone: 2 },
    { char: "煤", tone: 2 },
    { char: "眉", tone: 2 },
    { char: "每", tone: 3 },
    { char: "美", tone: 3 },
    { char: "妹", tone: 4 },
    { char: "魅", tone: 4 },
  ],
  men: [
    { char: "门", tone: 2 },
    { char: "们", tone: 5 },
    { char: "闷", tone: 4 },
  ],
  meng: [
    { char: "蒙", tone: 2 },
    { char: "盟", tone: 2 },
    { char: "猛", tone: 3 },
    { char: "梦", tone: 4 },
    { char: "孟", tone: 4 },
  ],
  mi: [
    { char: "迷", tone: 2 },
    { char: "谜", tone: 2 },
    { char: "米", tone: 3 },
    { char: "密", tone: 4 },
    { char: "秘", tone: 4 },
  ],
  mian: [
    { char: "棉", tone: 2 },
    { char: "眠", tone: 2 },
    { char: "免", tone: 3 },
    { char: "勉", tone: 3 },
    { char: "面", tone: 4 },
  ],
  miao: [
    { char: "苗", tone: 2 },
    { char: "秒", tone: 3 },
    { char: "妙", tone: 4 },
    { char: "庙", tone: 4 },
  ],
  mie: [{ char: "灭", tone: 4 }],
  min: [
    { char: "民", tone: 2 },
    { char: "敏", tone: 3 },
  ],
  ming: [
    { char: "名", tone: 2 },
    { char: "明", tone: 2 },
    { char: "命", tone: 4 },
  ],
  miu: [{ char: "谬", tone: 4 }],
  mo: [
    { char: "摸", tone: 1 },
    { char: "模", tone: 2 },
    { char: "磨", tone: 2 },
    { char: "魔", tone: 2 },
    { char: "莫", tone: 4 },
    { char: "墨", tone: 4 },
    { char: "默", tone: 4 },
    { char: "末", tone: 4 },
  ],
  mou: [
    { char: "谋", tone: 2 },
    { char: "某", tone: 3 },
  ],
  mu: [
    { char: "母", tone: 3 },
    { char: "亩", tone: 3 },
    { char: "木", tone: 4 },
    { char: "目", tone: 4 },
    { char: "牧", tone: 4 },
    { char: "幕", tone: 4 },
  ],
  na: [
    { char: "那", tone: 4 },
    { char: "哪", tone: 3 },
    { char: "纳", tone: 4 },
    { char: "拿", tone: 2 },
  ],
  nai: [
    { char: "奶", tone: 3 },
    { char: "乃", tone: 3 },
    { char: "耐", tone: 4 },
  ],
  nan: [
    { char: "男", tone: 2 },
    { char: "南", tone: 2 },
    { char: "难", tone: 2 },
    { char: "难", tone: 4 },
  ],
  nang: [{ char: "囊", tone: 2 }],
  nao: [
    { char: "脑", tone: 3 },
    { char: "恼", tone: 3 },
    { char: "闹", tone: 4 },
  ],
  ne: [{ char: "呢", tone: 5 }],
  nei: [{ char: "内", tone: 4 }],
  nen: [{ char: "嫩", tone: 4 }],
  neng: [{ char: "能", tone: 2 }],
  ni: [
    { char: "泥", tone: 2 },
    { char: "尼", tone: 2 },
    { char: "你", tone: 3 },
    { char: "逆", tone: 4 },
    { char: "腻", tone: 4 },
  ],
  nian: [
    { char: "年", tone: 2 },
    { char: "念", tone: 4 },
  ],
  niang: [
    { char: "娘", tone: 2 },
    { char: "酿", tone: 4 },
  ],
  niao: [
    { char: "鸟", tone: 3 },
    { char: "尿", tone: 4 },
  ],
  nie: [
    { char: "捏", tone: 1 },
    { char: "聂", tone: 4 },
  ],
  nin: [{ char: "您", tone: 2 }],
  ning: [
    { char: "宁", tone: 2 },
    { char: "凝", tone: 2 },
    { char: "宁", tone: 4 },
  ],
  niu: [
    { char: "牛", tone: 2 },
    { char: "扭", tone: 3 },
    { char: "拗", tone: 4 },
  ],
  nong: [
    { char: "农", tone: 2 },
    { char: "浓", tone: 2 },
    { char: "弄", tone: 4 },
  ],
  nu: [
    { char: "奴", tone: 2 },
    { char: "努", tone: 3 },
    { char: "怒", tone: 4 },
  ],
  nv: [{ char: "女", tone: 3 }],
  nuan: [{ char: "暖", tone: 3 }],
  nue: [{ char: "虐", tone: 4 }],
  nuo: [
    { char: "挪", tone: 2 },
    { char: "诺", tone: 4 },
  ],
  o: [
    { char: "哦", tone: 1 },
    { char: "哦", tone: 2 },
    { char: "哦", tone: 4 },
  ],
  ou: [
    { char: "区", tone: 1 },
    { char: "欧", tone: 1 },
    { char: "偶", tone: 3 },
    { char: "呕", tone: 3 },
  ],
  pa: [
    { char: "趴", tone: 1 },
    { char: "爬", tone: 2 },
    { char: "怕", tone: 4 },
  ],
  pai: [
    { char: "拍", tone: 1 },
    { char: "排", tone: 2 },
    { char: "牌", tone: 2 },
    { char: "派", tone: 4 },
  ],
  pan: [
    { char: "攀", tone: 1 },
    { char: "盘", tone: 2 },
    { char: "盼", tone: 4 },
    { char: "判", tone: 4 },
  ],
  pang: [
    { char: "胖", tone: 4 },
    { char: "旁", tone: 2 },
  ],
  pao: [
    { char: "抛", tone: 1 },
    { char: "跑", tone: 3 },
    { char: "泡", tone: 4 },
    { char: "炮", tone: 4 },
  ],
  pei: [
    { char: "陪", tone: 2 },
    { char: "培", tone: 2 },
    { char: "配", tone: 4 },
    { char: "佩", tone: 4 },
  ],
  pen: [
    { char: "喷", tone: 1 },
    { char: "盆", tone: 2 },
  ],
  peng: [
    { char: "烹", tone: 1 },
    { char: "朋", tone: 2 },
    { char: "彭", tone: 2 },
    { char: "棚", tone: 2 },
    { char: "碰", tone: 4 },
  ],
  pi: [
    { char: "批", tone: 1 },
    { char: "披", tone: 1 },
    { char: "皮", tone: 2 },
    { char: "脾", tone: 2 },
    { char: "匹", tone: 3 },
    { char: "屁", tone: 4 },
  ],
  pian: [
    { char: "篇", tone: 1 },
    { char: "片", tone: 4 },
    { char: "骗", tone: 4 },
  ],
  piao: [
    { char: "飘", tone: 1 },
    { char: "票", tone: 4 },
  ],
  pie: [
    { char: "撇", tone: 1 },
    { char: "瞥", tone: 1 },
  ],
  pin: [
    { char: "拼", tone: 1 },
    { char: "贫", tone: 2 },
    { char: "品", tone: 3 },
    { char: "聘", tone: 4 },
  ],
  ping: [
    { char: "平", tone: 2 },
    { char: "评", tone: 2 },
    { char: "瓶", tone: 2 },
  ],
  po: [
    { char: "泼", tone: 1 },
    { char: "婆", tone: 2 },
    { char: "破", tone: 4 },
    { char: "迫", tone: 4 },
  ],
  pou: [{ char: "剖", tone: 1 }],
  pu: [
    { char: "扑", tone: 1 },
    { char: "铺", tone: 1 },
    { char: "普", tone: 3 },
    { char: "谱", tone: 3 },
    { char: "铺", tone: 4 },
    { char: "瀑", tone: 4 },
  ],
  qi: [
    { char: "七", tone: 1 },
    { char: "期", tone: 1 },
    { char: "妻", tone: 1 },
    { char: "其", tone: 2 },
    { char: "奇", tone: 2 },
    { char: "骑", tone: 2 },
    { char: "旗", tone: 2 },
    { char: "起", tone: 3 },
    { char: "启", tone: 3 },
    { char: "企", tone: 3 },
    { char: "气", tone: 4 },
    { char: "器", tone: 4 },
    { char: "汽", tone: 4 },
  ],
  qia: [
    { char: "掐", tone: 1 },
    { char: "恰", tone: 4 },
  ],
  qian: [
    { char: "千", tone: 1 },
    { char: "签", tone: 1 },
    { char: "铅", tone: 1 },
    { char: "前", tone: 2 },
    { char: "钱", tone: 2 },
    { char: "浅", tone: 3 },
    { char: "欠", tone: 4 },
  ],
  qiang: [
    { char: "枪", tone: 1 },
    { char: "墙", tone: 2 },
    { char: "抢", tone: 3 },
    { char: "呛", tone: 4 },
  ],
  qiao: [
    { char: "敲", tone: 1 },
    { char: "桥", tone: 2 },
    { char: "巧", tone: 3 },
    { char: "翘", tone: 4 },
  ],
  qie: [
    { char: "切", tone: 1 },
    { char: "且", tone: 3 },
    { char: "切", tone: 4 },
    { char: "窃", tone: 4 },
  ],
  qin: [
    { char: "亲", tone: 1 },
    { char: "琴", tone: 2 },
    { char: "勤", tone: 2 },
    { char: "寝", tone: 3 },
  ],
  qing: [
    { char: "青", tone: 1 },
    { char: "清", tone: 1 },
    { char: "轻", tone: 1 },
    { char: "情", tone: 2 },
    { char: "晴", tone: 2 },
    { char: "请", tone: 3 },
    { char: "庆", tone: 4 },
  ],
  qiong: [{ char: "穷", tone: 2 }],
  qiu: [
    { char: "秋", tone: 1 },
    { char: "丘", tone: 1 },
    { char: "球", tone: 2 },
    { char: "求", tone: 2 },
  ],
  qu: [
    { char: "区", tone: 1 },
    { char: "曲", tone: 1 },
    { char: "驱", tone: 1 },
    { char: "取", tone: 3 },
    { char: "曲", tone: 3 },
    { char: "去", tone: 4 },
    { char: "趣", tone: 4 },
  ],
  quan: [
    { char: "圈", tone: 1 },
    { char: "全", tone: 2 },
    { char: "权", tone: 2 },
    { char: "泉", tone: 2 },
    { char: "犬", tone: 3 },
    { char: "劝", tone: 4 },
  ],
  que: [
    { char: "缺", tone: 1 },
    { char: "却", tone: 4 },
    { char: "确", tone: 4 },
  ],
  qun: [{ char: "群", tone: 2 }],
  ran: [
    { char: "然", tone: 2 },
    { char: "染", tone: 3 },
  ],
  rang: [
    { char: "让", tone: 4 },
    { char: "嚷", tone: 3 },
  ],
  rao: [
    { char: "扰", tone: 3 },
    { char: "绕", tone: 4 },
  ],
  re: [{ char: "热", tone: 4 }],
  ren: [
    { char: "人", tone: 2 },
    { char: "仁", tone: 2 },
    { char: "忍", tone: 3 },
    { char: "认", tone: 4 },
    { char: "任", tone: 4 },
  ],
  reng: [{ char: "仍", tone: 2 }],
  ri: [{ char: "日", tone: 4 }],
  rong: [
    { char: "容", tone: 2 },
    { char: "荣", tone: 2 },
    { char: "融", tone: 2 },
    { char: "冗", tone: 3 },
  ],
  rou: [
    { char: "肉", tone: 4 },
    { char: "柔", tone: 2 },
  ],
  ru: [
    { char: "如", tone: 2 },
    { char: "入", tone: 4 },
  ],
  ruan: [{ char: "软", tone: 3 }],
  rui: [
    { char: "瑞", tone: 4 },
    { char: "锐", tone: 4 },
  ],
  run: [{ char: "润", tone: 4 }],
  ruo: [
    { char: "若", tone: 4 },
    { char: "弱", tone: 4 },
  ],
  sa: [
    { char: "撒", tone: 1 },
    { char: "洒", tone: 3 },
    { char: "萨", tone: 4 },
  ],
  sai: [
    { char: "塞", tone: 1 },
    { char: "赛", tone: 4 },
  ],
  san: [
    { char: "三", tone: 1 },
    { char: "散", tone: 3 },
    { char: "散", tone: 4 },
  ],
  sang: [
    { char: "桑", tone: 1 },
    { char: "嗓", tone: 3 },
    { char: "丧", tone: 4 },
  ],
  sao: [
    { char: "扫", tone: 3 },
    { char: "嫂", tone: 3 },
    { char: "扫", tone: 4 },
  ],
  se: [{ char: "色", tone: 4 }],
  sen: [{ char: "森", tone: 1 }],
  seng: [{ char: "僧", tone: 1 }],
  sha: [
    { char: "杀", tone: 1 },
    { char: "沙", tone: 1 },
    { char: "傻", tone: 3 },
    { char: "厦", tone: 4 },
  ],
  shai: [
    { char: "筛", tone: 1 },
    { char: "晒", tone: 4 },
  ],
  shan: [
    { char: "山", tone: 1 },
    { char: "衫", tone: 1 },
    { char: "闪", tone: 3 },
    { char: "善", tone: 4 },
    { char: "扇", tone: 4 },
  ],
  shang: [
    { char: "伤", tone: 1 },
    { char: "商", tone: 1 },
    { char: "上", tone: 4 },
    { char: "尚", tone: 4 },
    { char: "赏", tone: 3 },
  ],
  shao: [
    { char: "烧", tone: 1 },
    { char: "稍", tone: 1 },
    { char: "少", tone: 3 },
    { char: "少", tone: 4 },
    { char: "绍", tone: 4 },
  ],
  she: [
    { char: "蛇", tone: 2 },
    { char: "舌", tone: 2 },
    { char: "舍", tone: 3 },
    { char: "社", tone: 4 },
    { char: "射", tone: 4 },
    { char: "设", tone: 4 },
  ],
  shei: [{ char: "谁", tone: 2 }],
  shen: [
    { char: "身", tone: 1 },
    { char: "深", tone: 1 },
    { char: "神", tone: 2 },
    { char: "审", tone: 3 },
    { char: "甚", tone: 4 },
    { char: "慎", tone: 4 },
  ],
  sheng: [
    { char: "生", tone: 1 },
    { char: "声", tone: 1 },
    { char: "省", tone: 3 },
    { char: "胜", tone: 4 },
    { char: "剩", tone: 4 },
  ],
  shi: [
    { char: "师", tone: 1 },
    { char: "诗", tone: 1 },
    { char: "湿", tone: 1 },
    { char: "十", tone: 2 },
    { char: "时", tone: 2 },
    { char: "食", tone: 2 },
    { char: "石", tone: 2 },
    { char: "史", tone: 3 },
    { char: "使", tone: 3 },
    { char: "始", tone: 3 },
    { char: "是", tone: 4 },
    { char: "市", tone: 4 },
    { char: "事", tone: 4 },
    { char: "试", tone: 4 },
    { char: "世", tone: 4 },
  ],
  shou: [
    { char: "收", tone: 1 },
    { char: "手", tone: 3 },
    { char: "首", tone: 3 },
    { char: "受", tone: 4 },
    { char: "售", tone: 4 },
    { char: "寿", tone: 4 },
  ],
  shu: [
    { char: "书", tone: 1 },
    { char: "输", tone: 1 },
    { char: "舒", tone: 1 },
    { char: "熟", tone: 2 },
    { char: "数", tone: 3 },
    { char: "属", tone: 3 },
    { char: "树", tone: 4 },
    { char: "数", tone: 4 },
    { char: "术", tone: 4 },
    { char: "述", tone: 4 },
  ],
  shua: [
    { char: "刷", tone: 1 },
    { char: "耍", tone: 3 },
  ],
  shuai: [
    { char: "摔", tone: 1 },
    { char: "帅", tone: 4 },
  ],
  shuan: [
    { char: "拴", tone: 1 },
    { char: "涮", tone: 4 },
  ],
  shuang: [
    { char: "双", tone: 1 },
    { char: "爽", tone: 3 },
  ],
  shui: [
    { char: "水", tone: 3 },
    { char: "睡", tone: 4 },
    { char: "税", tone: 4 },
  ],
  shun: [
    { char: "顺", tone: 4 },
    { char: "瞬", tone: 4 },
  ],
  shuo: [
    { char: "说", tone: 1 },
    { char: "数", tone: 4 },
  ],
  si: [
    { char: "思", tone: 1 },
    { char: "私", tone: 1 },
    { char: "死", tone: 3 },
    { char: "四", tone: 4 },
    { char: "似", tone: 4 },
  ],
  song: [
    { char: "松", tone: 1 },
    { char: "送", tone: 4 },
    { char: "颂", tone: 4 },
  ],
  sou: [
    { char: "搜", tone: 1 },
    { char: "艘", tone: 1 },
  ],
  su: [
    { char: "苏", tone: 1 },
    { char: "素", tone: 4 },
    { char: "速", tone: 4 },
    { char: "诉", tone: 4 },
  ],
  suan: [
    { char: "酸", tone: 1 },
    { char: "算", tone: 4 },
  ],
  sui: [
    { char: "虽", tone: 1 },
    { char: "随", tone: 2 },
    { char: "岁", tone: 4 },
    { char: "碎", tone: 4 },
  ],
  sun: [
    { char: "孙", tone: 1 },
    { char: "损", tone: 3 },
  ],
  suo: [
    { char: "缩", tone: 1 },
    { char: "所", tone: 3 },
    { char: "锁", tone: 3 },
  ],
  ta: [
    { char: "他", tone: 1 },
    { char: "她", tone: 1 },
    { char: "它", tone: 1 },
    { char: "塔", tone: 3 },
    { char: "踏", tone: 4 },
  ],
  tai: [
    { char: "胎", tone: 1 },
    { char: "台", tone: 2 },
    { char: "太", tone: 4 },
    { char: "态", tone: 4 },
  ],
  tan: [
    { char: "贪", tone: 1 },
    { char: "谈", tone: 2 },
    { char: "弹", tone: 2 },
    { char: "坦", tone: 3 },
    { char: "探", tone: 4 },
  ],
  tang: [
    { char: "汤", tone: 1 },
    { char: "糖", tone: 2 },
    { char: "堂", tone: 2 },
    { char: "躺", tone: 3 },
    { char: "烫", tone: 4 },
  ],
  tao: [
    { char: "桃", tone: 2 },
    { char: "逃", tone: 2 },
    { char: "讨", tone: 3 },
    { char: "套", tone: 4 },
  ],
  te: [{ char: "特", tone: 4 }],
  teng: [
    { char: "疼", tone: 2 },
    { char: "腾", tone: 2 },
  ],
  ti: [
    { char: "踢", tone: 1 },
    { char: "提", tone: 2 },
    { char: "体", tone: 3 },
    { char: "替", tone: 4 },
  ],
  tian: [
    { char: "天", tone: 1 },
    { char: "甜", tone: 2 },
    { char: "田", tone: 2 },
    { char: "填", tone: 2 },
  ],
  tiao: [
    { char: "挑", tone: 1 },
    { char: "条", tone: 2 },
    { char: "跳", tone: 4 },
  ],
  tie: [
    { char: "贴", tone: 1 },
    { char: "铁", tone: 3 },
  ],
  ting: [
    { char: "听", tone: 1 },
    { char: "停", tone: 2 },
    { char: "庭", tone: 2 },
    { char: "挺", tone: 3 },
  ],
  tong: [
    { char: "通", tone: 1 },
    { char: "同", tone: 2 },
    { char: "铜", tone: 2 },
    { char: "桶", tone: 3 },
    { char: "痛", tone: 4 },
  ],
  tou: [
    { char: "偷", tone: 1 },
    { char: "头", tone: 2 },
    { char: "透", tone: 4 },
  ],
  tu: [
    { char: "突", tone: 1 },
    { char: "图", tone: 2 },
    { char: "土", tone: 3 },
    { char: "吐", tone: 4 },
  ],
  tuan: [{ char: "团", tone: 2 }],
  tui: [
    { char: "推", tone: 1 },
    { char: "腿", tone: 3 },
    { char: "退", tone: 4 },
  ],
  tun: [
    { char: "吞", tone: 1 },
    { char: "屯", tone: 2 },
    { char: "褪", tone: 4 },
  ],
  tuo: [
    { char: "脱", tone: 1 },
    { char: "驮", tone: 2 },
    { char: "妥", tone: 3 },
    { char: "拓", tone: 4 },
  ],
  wa: [
    { char: "蛙", tone: 1 },
    { char: "挖", tone: 1 },
    { char: "娃", tone: 2 },
    { char: "瓦", tone: 3 },
    { char: "袜", tone: 4 },
  ],
  wai: [
    { char: "歪", tone: 1 },
    { char: "外", tone: 4 },
  ],
  wan: [
    { char: "弯", tone: 1 },
    { char: "完", tone: 2 },
    { char: "玩", tone: 2 },
    { char: "晚", tone: 3 },
    { char: "碗", tone: 3 },
    { char: "万", tone: 4 },
  ],
  wang: [
    { char: "王", tone: 2 },
    { char: "网", tone: 3 },
    { char: "往", tone: 3 },
    { char: "忘", tone: 4 },
    { char: "望", tone: 4 },
  ],
  wei: [
    { char: "微", tone: 1 },
    { char: "危", tone: 1 },
    { char: "为", tone: 2 },
    { char: "围", tone: 2 },
    { char: "委", tone: 3 },
    { char: "伟", tone: 3 },
    { char: "位", tone: 4 },
    { char: "未", tone: 4 },
    { char: "卫", tone: 4 },
  ],
  wen: [
    { char: "温", tone: 1 },
    { char: "文", tone: 2 },
    { char: "闻", tone: 2 },
    { char: "稳", tone: 3 },
    { char: "问", tone: 4 },
  ],
  weng: [
    { char: "翁", tone: 1 },
    { char: "瓮", tone: 4 },
  ],
  wo: [
    { char: "我", tone: 3 },
    { char: "握", tone: 4 },
  ],
  wu: [
    { char: "屋", tone: 1 },
    { char: "乌", tone: 1 },
    { char: "无", tone: 2 },
    { char: "五", tone: 3 },
    { char: "午", tone: 3 },
    { char: "物", tone: 4 },
    { char: "务", tone: 4 },
  ],
  xi: [
    { char: "西", tone: 1 },
    { char: "希", tone: 1 },
    { char: "习", tone: 2 },
    { char: "喜", tone: 3 },
    { char: "洗", tone: 3 },
    { char: "细", tone: 4 },
    { char: "系", tone: 4 },
  ],
  xia: [
    { char: "下", tone: 4 },
    { char: "夏", tone: 4 },
    { char: "虾", tone: 1 },
    { char: "吓", tone: 4 },
  ],
  xian: [
    { char: "先", tone: 1 },
    { char: "鲜", tone: 1 },
    { char: "线", tone: 4 },
    { char: "现", tone: 4 },
    { char: "县", tone: 4 },
    { char: "险", tone: 3 },
    { char: "显", tone: 3 },
  ],
  xiang: [
    { char: "香", tone: 1 },
    { char: "相", tone: 1 },
    { char: "想", tone: 3 },
    { char: "响", tone: 3 },
    { char: "向", tone: 4 },
    { char: "象", tone: 4 },
  ],
  xiao: [
    { char: "消", tone: 1 },
    { char: "小", tone: 3 },
    { char: "笑", tone: 4 },
    { char: "效", tone: 4 },
  ],
  xie: [
    { char: "些", tone: 1 },
    { char: "鞋", tone: 2 },
    { char: "写", tone: 3 },
    { char: "谢", tone: 4 },
    { char: "血", tone: 4 },
  ],
  xin: [
    { char: "心", tone: 1 },
    { char: "新", tone: 1 },
    { char: "信", tone: 4 },
  ],
  xing: [
    { char: "星", tone: 1 },
    { char: "行", tone: 2 },
    { char: "醒", tone: 3 },
    { char: "性", tone: 4 },
    { char: "姓", tone: 4 },
  ],
  xiong: [
    { char: "兄", tone: 1 },
    { char: "胸", tone: 1 },
    { char: "雄", tone: 2 },
  ],
  xiu: [
    { char: "休", tone: 1 },
    { char: "修", tone: 1 },
    { char: "秀", tone: 4 },
  ],
  xu: [
    { char: "需", tone: 1 },
    { char: "许", tone: 3 },
    { char: "续", tone: 4 },
    { char: "序", tone: 4 },
  ],
  xuan: [
    { char: "宣", tone: 1 },
    { char: "选", tone: 3 },
    { char: "炫", tone: 4 },
  ],
  xue: [
    { char: "学", tone: 2 },
    { char: "雪", tone: 3 },
    { char: "血", tone: 4 },
  ],
  xun: [
    { char: "寻", tone: 2 },
    { char: "训", tone: 4 },
  ],
  ya: [
    { char: "压", tone: 1 },
    { char: "鸭", tone: 1 },
    { char: "牙", tone: 2 },
    { char: "哑", tone: 3 },
    { char: "亚", tone: 4 },
  ],
  yan: [
    { char: "烟", tone: 1 },
    { char: "言", tone: 2 },
    { char: "延", tone: 2 },
    { char: "眼", tone: 3 },
    { char: "验", tone: 4 },
    { char: "宴", tone: 4 },
  ],
  yang: [
    { char: "央", tone: 1 },
    { char: "羊", tone: 2 },
    { char: "阳", tone: 2 },
    { char: "养", tone: 3 },
    { char: "样", tone: 4 },
  ],
  yao: [
    { char: "要", tone: 1 },
    { char: "摇", tone: 2 },
    { char: "咬", tone: 3 },
    { char: "药", tone: 4 },
    { char: "要", tone: 4 },
  ],
  ye: [
    { char: "也", tone: 3 },
    { char: "野", tone: 3 },
    { char: "夜", tone: 4 },
    { char: "业", tone: 4 },
  ],
  yi: [
    { char: "一", tone: 1 },
    { char: "衣", tone: 1 },
    { char: "宜", tone: 2 },
    { char: "以", tone: 3 },
    { char: "已", tone: 3 },
    { char: "意", tone: 4 },
    { char: "义", tone: 4 },
    { char: "易", tone: 4 },
  ],
  yin: [
    { char: "音", tone: 1 },
    { char: "阴", tone: 1 },
    { char: "银", tone: 2 },
    { char: "引", tone: 3 },
    { char: "印", tone: 4 },
  ],
  ying: [
    { char: "英", tone: 1 },
    { char: "营", tone: 2 },
    { char: "影", tone: 3 },
    { char: "应", tone: 4 },
    { char: "硬", tone: 4 },
  ],
  yo: [{ char: "哟", tone: 1 }],
  yong: [
    { char: "用", tone: 4 },
    { char: "永", tone: 3 },
    { char: "勇", tone: 3 },
    { char: "拥", tone: 1 },
  ],
  you: [
    { char: "优", tone: 1 },
    { char: "由", tone: 2 },
    { char: "油", tone: 2 },
    { char: "有", tone: 3 },
    { char: "又", tone: 4 },
    { char: "右", tone: 4 },
  ],
  yu: [
    { char: "鱼", tone: 2 },
    { char: "于", tone: 2 },
    { char: "雨", tone: 3 },
    { char: "语", tone: 3 },
    { char: "与", tone: 4 },
    { char: "育", tone: 4 },
  ],
  yuan: [
    { char: "员", tone: 2 },
    { char: "元", tone: 2 },
    { char: "园", tone: 2 },
    { char: "远", tone: 3 },
    { char: "院", tone: 4 },
    { char: "愿", tone: 4 },
  ],
  yue: [
    { char: "月", tone: 4 },
    { char: "乐", tone: 4 },
    { char: "越", tone: 4 },
  ],
  yun: [
    { char: "云", tone: 2 },
    { char: "允", tone: 3 },
    { char: "运", tone: 4 },
  ],
  za: [{ char: "杂", tone: 2 }],
  zai: [
    { char: "灾", tone: 1 },
    { char: "在", tone: 4 },
    { char: "再", tone: 4 },
  ],
  zan: [
    { char: "咱", tone: 2 },
    { char: "攒", tone: 3 },
    { char: "赞", tone: 4 },
  ],
  zang: [
    { char: "脏", tone: 1 },
    { char: "葬", tone: 4 },
  ],
  zao: [
    { char: "糟", tone: 1 },
    { char: "早", tone: 3 },
    { char: "澡", tone: 3 },
    { char: "造", tone: 4 },
    { char: "燥", tone: 4 },
  ],
  ze: [
    { char: "则", tone: 2 },
    { char: "责", tone: 2 },
  ],
  zei: [{ char: "贼", tone: 2 }],
  zen: [{ char: "怎", tone: 3 }],
  zeng: [
    { char: "增", tone: 1 },
    { char: "赠", tone: 4 },
  ],
  zha: [
    { char: "扎", tone: 1 },
    { char: "炸", tone: 2 },
    { char: "眨", tone: 3 },
    { char: "炸", tone: 4 },
  ],
  zhai: [
    { char: "摘", tone: 1 },
    { char: "宅", tone: 2 },
    { char: "窄", tone: 3 },
    { char: "债", tone: 4 },
  ],
  zhan: [
    { char: "占", tone: 1 },
    { char: "展", tone: 3 },
    { char: "战", tone: 4 },
    { char: "站", tone: 4 },
  ],
  zhang: [
    { char: "张", tone: 1 },
    { char: "长", tone: 3 },
    { char: "掌", tone: 3 },
    { char: "丈", tone: 4 },
    { char: "账", tone: 4 },
  ],
  zhao: [
    { char: "招", tone: 1 },
    { char: "着", tone: 2 },
    { char: "找", tone: 3 },
    { char: "照", tone: 4 },
    { char: "赵", tone: 4 },
  ],
  zhe: [
    { char: "折", tone: 1 },
    { char: "者", tone: 3 },
    { char: "这", tone: 4 },
  ],
  zhei: [{ char: "这", tone: 4 }],
  zhen: [
    { char: "真", tone: 1 },
    { char: "针", tone: 1 },
    { char: "诊", tone: 3 },
    { char: "镇", tone: 4 },
    { char: "阵", tone: 4 },
  ],
  zheng: [
    { char: "正", tone: 1 },
    { char: "争", tone: 1 },
    { char: "整", tone: 3 },
    { char: "正", tone: 4 },
    { char: "政", tone: 4 },
    { char: "证", tone: 4 },
  ],
  zhi: [
    { char: "知", tone: 1 },
    { char: "之", tone: 1 },
    { char: "直", tone: 2 },
    { char: "值", tone: 2 },
    { char: "只", tone: 3 },
    { char: "纸", tone: 3 },
    { char: "至", tone: 4 },
    { char: "制", tone: 4 },
    { char: "志", tone: 4 },
  ],
  zhong: [
    { char: "中", tone: 1 },
    { char: "钟", tone: 1 },
    { char: "种", tone: 3 },
    { char: "重", tone: 4 },
    { char: "众", tone: 4 },
  ],
  zhou: [
    { char: "周", tone: 1 },
    { char: "轴", tone: 2 },
    { char: "肘", tone: 3 },
    { char: "宙", tone: 4 },
  ],
  zhu: [
    { char: "猪", tone: 1 },
    { char: "主", tone: 3 },
    { char: "住", tone: 4 },
    { char: "注", tone: 4 },
    { char: "助", tone: 4 },
  ],
  zhua: [
    { char: "抓", tone: 1 },
    { char: "爪", tone: 3 },
  ],
  zhuai: [{ char: "拽", tone: 4 }],
  zhuan: [
    { char: "专", tone: 1 },
    { char: "转", tone: 3 },
    { char: "转", tone: 4 },
    { char: "赚", tone: 4 },
  ],
  zhuang: [
    { char: "装", tone: 1 },
    { char: "撞", tone: 4 },
    { char: "壮", tone: 4 },
  ],
  zhui: [
    { char: "追", tone: 1 },
    { char: "坠", tone: 4 },
  ],
  zhun: [{ char: "准", tone: 3 }],
  zhuo: [
    { char: "桌", tone: 1 },
    { char: "着", tone: 2 },
  ],
  zi: [
    { char: "资", tone: 1 },
    { char: "子", tone: 3 },
    { char: "自", tone: 4 },
    { char: "字", tone: 4 },
  ],
  zong: [
    { char: "总", tone: 3 },
    { char: "纵", tone: 4 },
  ],
  zou: [
    { char: "走", tone: 3 },
    { char: "奏", tone: 4 },
  ],
  zu: [
    { char: "租", tone: 1 },
    { char: "族", tone: 2 },
    { char: "组", tone: 3 },
  ],
  zuan: [
    { char: "钻", tone: 1 },
    { char: "钻", tone: 4 },
  ],
  zui: [
    { char: "嘴", tone: 3 },
    { char: "最", tone: 4 },
    { char: "醉", tone: 4 },
  ],
  zun: [{ char: "尊", tone: 1 }],
  zuo: [
    { char: "昨", tone: 2 },
    { char: "左", tone: 3 },
    { char: "做", tone: 4 },
    { char: "坐", tone: 4 },
    { char: "作", tone: 4 },
  ],
};

const dictMap = new Map<string, CharEntry[]>(Object.entries(raw));

export function getCandidates(syllable: string, tone?: number): CharEntry[] {
  const entries = dictMap.get(syllable.toLowerCase()) || [];
  if (tone !== undefined && tone >= 1 && tone <= 5) {
    return entries.filter((e) => e.tone === tone);
  }
  // Sort by tone (1-4 then 5) to put the most common tones first
  return [...entries].sort((a, b) => a.tone - b.tone);
}

// Set of all valid pinyin base syllables (for optional segmentation)
export const VALID_SYLLABLES: Set<string> = new Set(Object.keys(raw));
